import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@web/components/Button';
import { ErrorState, LoadingBar } from '@web/components/Feedback';
import { ReasonChip } from '@web/components/ReasonChip';
import { StateChip } from '@web/components/StateChip';
import { TemperatureMeter } from '@web/components/TemperatureMeter';
import { WindowCountdown } from '@web/components/Timers';
import { useSession } from '@web/hooks/useSession';
import { useVisualViewport } from '@web/hooks/useVisualViewport';
import { Composer } from './Composer';
import { useHandback, useResolve, useTakeOver } from './mutations';
import { Transcript, type TranscriptHandle } from './Transcript';
import { useConversation, useWatchThread } from './useThread';

/**
 * One conversation, full screen.
 *
 * The shell is a three-row grid — header / transcript / composer — sized from
 * `--app-h`, which `useVisualViewport` keeps in step with the iOS keyboard. A
 * `position: fixed` composer would sit behind that keyboard, which is the
 * single most common way a chat UI breaks on a phone.
 */
export function ThreadPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const conversation = useConversation(id);
  const transcript = useRef<TranscriptHandle | null>(null);

  useWatchThread(id);
  useVisualViewport();

  const takeOver = useTakeOver();
  const handback = useHandback();
  const resolve = useResolve();
  const [handbackNote, setHandbackNote] = useState('');
  const [handingBack, setHandingBack] = useState(false);

  if (conversation.isPending) return <LoadingBar label="Loading conversation" />;
  if (conversation.error !== null) {
    return (
      <ErrorState
        error={conversation.error}
        onRetry={() => {
          void conversation.refetch();
        }}
      />
    );
  }

  const detail = conversation.data;
  const me = session.data;
  const mine = detail.assignee !== null && me != null && detail.assignee.id === me.id;
  const heldBySomeoneElse = detail.handlingState === 'human' && !mine;

  const replyBlockedReason =
    detail.handlingState === 'human' && mine
      ? undefined
      : heldBySomeoneElse
        ? `${detail.assignee?.name ?? 'A colleague'} is holding this conversation. Take it over to reply.`
        : 'The assistant is handling this conversation. Take over to reply yourself.';

  return (
    /* Three rows: header, transcript, composer. The height comes from the shell,
       which is already tracking the visual viewport for us. */
    <div className="grid h-full grid-rows-[auto_1fr_auto] bg-ground">
      <header className="border-b border-rule bg-panel">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={() => {
              void navigate(-1);
            }}
            aria-label="Back"
            className="flex size-8 shrink-0 items-center justify-center border border-rule-strong font-mono text-ink-dim hover:bg-hover md:hidden"
          >
            <span aria-hidden>←</span>
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <h1 className="truncate text-sm font-semibold">
                {detail.contact.name ?? detail.contact.pushName ?? detail.contact.phone}
              </h1>
              <span className="shrink-0 font-mono text-[11px] text-ink-faint tabular-nums">
                {detail.contact.phone}
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-faint">
              <span className="truncate">{detail.trade ?? 'trade unknown'}</span>
              <TemperatureMeter temperature={detail.temperature} />
              <WindowCountdown lastInboundAt={detail.lastInboundAt} size="full" />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <StateChip state={detail.handlingState} />
            {detail.assignee !== null && (
              <span className="hidden font-mono text-[11px] text-ink-dim md:inline">
                {detail.assignee.name}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-rule px-3 py-2">
          {detail.handlingState === 'parked' && (
            <ReasonChip reason={detail.parkReason} size="full" />
          )}
          {detail.parkSummary !== null && detail.handlingState === 'parked' && (
            <p className="min-w-0 flex-1 truncate text-[11px] text-ink-dim">{detail.parkSummary}</p>
          )}

          <div className="ml-auto flex items-center gap-2">
            {!mine && (
              <Button
                tone="primary"
                size="touch"
                disabled={takeOver.isPending}
                onClick={() => {
                  takeOver.mutate({ conversationId: id, body: { reason: 'manual' } });
                }}
              >
                {takeOver.isPending ? 'Taking over…' : 'Take over'}
              </Button>
            )}
            {mine && (
              <>
                <Button
                  size="touch"
                  onClick={() => {
                    setHandingBack((open) => !open);
                  }}
                  aria-expanded={handingBack}
                >
                  Return to AI
                </Button>
                <Button
                  size="touch"
                  disabled={resolve.isPending}
                  onClick={() => {
                    resolve.mutate(
                      { conversationId: id },
                      {
                        onSuccess: () => {
                          void navigate('/board');
                        },
                      },
                    );
                  }}
                >
                  Resolve
                </Button>
              </>
            )}
          </div>
        </div>

        {handingBack && (
          <div className="border-t border-rule bg-ground px-3 py-2">
            <label
              className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase"
              htmlFor="handback-note"
            >
              Instruction for the assistant
            </label>
            <textarea
              id="handback-note"
              rows={2}
              value={handbackNote}
              onChange={(event) => {
                setHandbackNote(event.target.value);
              }}
              placeholder="e.g. I quoted the Qatar rate; continue collecting documents."
              className="mt-1 w-full border border-rule-strong bg-panel px-2 py-1.5"
            />
            <p className="mt-1 text-[11px] text-ink-faint">
              This replaces any previous instruction — the assistant reads it instead of the
              transcript.
            </p>
            <div className="mt-2 flex justify-end gap-2">
              <Button
                size="touch"
                onClick={() => {
                  setHandingBack(false);
                }}
              >
                Cancel
              </Button>
              <Button
                tone="primary"
                size="touch"
                disabled={handback.isPending}
                onClick={() => {
                  handback.mutate(
                    {
                      conversationId: id,
                      body: {
                        resume: 'resume',
                        ...(handbackNote.trim() === '' ? {} : { note: handbackNote.trim() }),
                      },
                    },
                    {
                      onSuccess: () => {
                        setHandingBack(false);
                        setHandbackNote('');
                      },
                    },
                  );
                }}
              >
                Hand back
              </Button>
            </div>
          </div>
        )}
      </header>

      <div className="min-h-0">
        <Transcript
          conversationId={id}
          handleRef={transcript}
          authorNames={
            detail.assignee === null ? undefined : { [detail.assignee.id]: detail.assignee.name }
          }
        />
      </div>

      <Composer
        conversationId={id}
        lastInboundAt={detail.lastInboundAt}
        replyBlockedReason={replyBlockedReason}
        onSent={() => {
          transcript.current?.scrollToBottom();
        }}
      />
    </div>
  );
}
